import { t } from "@lingui/macro";
import { captureException } from "@sentry/react-native";
import graphql from "babel-plugin-relay/macro";
import { useAtom, useAtomValue } from "jotai";
import { useCallback, useEffect } from "react";
import { useMutation } from "react-relay";

import { useTrackEvent } from "@/utils/Analytics/Mixpanel/hooks/useTrackEvent";
import { useToast } from "@whoosh/design-system/src/components/overlay/Toast/useToast";

import { bookingSummaryAnalyticsDataAtom } from "../../atoms/analytics/bookingSummaryAnalyticsDataAtom";
import { isPaymentMethodEntryUpdatingAtom } from "../../atoms/stores";
import { useUpdatePaymentMethodOnBookingMutation } from "./__generated__/useUpdatePaymentMethodOnBookingMutation.graphql";

export const useUpdatePaymentMethodOnBooking = ({
  bookingId,
  successCallback,
  errorCallback,
}: {
  bookingId: string;
  successCallback: (paymentCardOnFileId: string | undefined) => void;
  errorCallback?: () => void;
}) => {
  const [updatePaymentMethodOnBooking, isUpdatingPaymentMethod] =
    useMutation<useUpdatePaymentMethodOnBookingMutation>(
      useUpdatePaymentMethodOnBookingString
    );

  const { showToast } = useToast();

  const [isUpdatingAtomValue, setIsUpdatingAtomValue] = useAtom(
    isPaymentMethodEntryUpdatingAtom
  );

  useEffect(() => {
    if (isUpdatingAtomValue !== isUpdatingPaymentMethod) {
      setIsUpdatingAtomValue(isUpdatingPaymentMethod);
    }
  }, [isUpdatingPaymentMethod, isUpdatingAtomValue, setIsUpdatingAtomValue]);

  const analyticsData = useAtomValue(bookingSummaryAnalyticsDataAtom);
  const trackEvent = useTrackEvent();

  const handleUpdateBookingPaymentMethod = useCallback(
    ({ paymentCardOnFileId }: { paymentCardOnFileId: string }) => {
      updatePaymentMethodOnBooking({
        variables: {
          input: { paymentCardOnFileId, bookingId },
        },
        onCompleted: (res, errors) => {
          if (
            errors?.length ||
            !res.responsePayload?.success ||
            !res.responsePayload.booking?.paymentCardOnFile?.id
          ) {
            errorCallback?.();
            return;
          }
          trackEvent("BookingDetail.PaymentMethodUpdated", analyticsData ?? {});
          successCallback(res.responsePayload?.booking?.paymentCardOnFile?.id);
          showToast({
            title: t`Payment method updated`,
            semanticStyle: "success",
            toastId: "payment-method-updated",
          });
        },
        onError: (error) => {
          captureException(error);
          errorCallback?.();
        },
      });
    },
    [
      updatePaymentMethodOnBooking,
      bookingId,
      trackEvent,
      analyticsData,
      successCallback,
      errorCallback,
      showToast,
    ]
  );

  return [handleUpdateBookingPaymentMethod, isUpdatingPaymentMethod] as const;
};

const useUpdatePaymentMethodOnBookingString = graphql`
  mutation useUpdatePaymentMethodOnBookingMutation(
    $input: UpdateBookingPaymentMethodInput!
  ) @relay_test_operation {
    responsePayload: updateBookingPaymentMethod(input: $input) {
      errors {
        code

        message
        field
      }
      success
      booking {
        id
        paymentCardOnFile {
          id
        }
      }
    }
  }
`;
